import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Satellite, 
  LocateOff,
  Users, 
  RefreshCw, 
  Clock, 
  ChevronDown, 
  ChevronUp,
  MapPin,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function GPSStatusOverview() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showInactiveList, setShowInactiveList] = useState(false);

  const fetchGPSStatus = async () => {
    try {
      const response = await axios.get(`${API}/drivers/gps-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching GPS status:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load GPS status');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGPSStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchGPSStatus, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const formatTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Satellite className="h-4 w-4 text-emerald-600" />
            GPS Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin h-5 w-5 border-2 border-emerald-600 border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const activePercentage = data.total_drivers > 0 
    ? Math.round((data.gps_active_count / data.total_drivers) * 100) 
    : 0;

  return (
    <Card data-testid="gps-status-overview">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Satellite className="h-4 w-4 text-emerald-600" />
            GPS Status Overview
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchGPSStatus}
            className="h-7 px-2"
            data-testid="refresh-gps-status-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        {lastUpdated && (
          <p className="text-xs text-slate-400">
            Updated: {lastUpdated.toLocaleTimeString()}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3">
          {/* Total Drivers */}
          <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="total-drivers-stat">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Users className="h-4 w-4 text-slate-500" />
            </div>
            <p className="text-2xl font-bold text-slate-700">{data.total_drivers}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          
          {/* GPS Active */}
          <div className="bg-emerald-50 rounded-lg p-3 text-center" data-testid="gps-active-stat">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Satellite className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-700">{data.gps_active_count}</p>
            <p className="text-xs text-emerald-600">GPS On</p>
          </div>
          
          {/* GPS Inactive */}
          <div className="bg-red-50 rounded-lg p-3 text-center" data-testid="gps-inactive-stat">
            <div className="flex items-center justify-center gap-1 mb-1">
              <LocateOff className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">{data.gps_inactive_count}</p>
            <p className="text-xs text-red-500">GPS Off</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">GPS Coverage</span>
            <span className={`font-medium ${activePercentage >= 70 ? 'text-emerald-600' : activePercentage >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
              {activePercentage}%
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                activePercentage >= 70 ? 'bg-emerald-500' : 
                activePercentage >= 40 ? 'bg-amber-500' : 
                'bg-red-500'
              }`}
              style={{ width: `${activePercentage}%` }}
              data-testid="gps-coverage-bar"
            />
          </div>
        </div>

        {/* Warning if many drivers have GPS off */}
        {data.gps_inactive_count > 0 && activePercentage < 50 && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="gps-warning">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              {data.gps_inactive_count} driver{data.gps_inactive_count > 1 ? 's' : ''} without GPS tracking. 
              This may affect ride verification and live tracking.
            </p>
          </div>
        )}

        {/* Inactive Drivers List (Collapsible) */}
        {data.gps_inactive_count > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowInactiveList(!showInactiveList)}
              className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
              data-testid="toggle-inactive-list"
            >
              <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <LocateOff className="h-4 w-4 text-red-500" />
                Drivers with GPS Off ({data.gps_inactive_count})
              </span>
              {showInactiveList ? (
                <ChevronUp className="h-4 w-4 text-slate-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-500" />
              )}
            </button>
            
            {showInactiveList && (
              <div className="divide-y divide-slate-100" data-testid="inactive-drivers-list">
                {data.drivers_without_gps.map((driver) => (
                  <div 
                    key={driver.driver_id} 
                    className="p-3 flex items-center justify-between hover:bg-slate-50"
                    data-testid={`inactive-driver-${driver.driver_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <LocateOff className="h-4 w-4 text-red-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{driver.driver_name}</p>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" />
                          <span>Last seen: {formatTime(driver.last_updated)}</span>
                        </div>
                      </div>
                    </div>
                    {driver.role === 'admin-driver' && (
                      <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-200">
                        Admin
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All drivers have GPS on message */}
        {data.gps_inactive_count === 0 && data.total_drivers > 0 && (
          <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="all-gps-active">
            <Satellite className="h-4 w-4 text-emerald-600" />
            <p className="text-xs text-emerald-700">
              All drivers have GPS tracking enabled
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
